import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Database, Filter, Link as LinkIcon, Plus, Search, TrendingUp, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const emptyFilters = {
  oem: '',
  region: '',
  platform: '',
  lifecycle_status: '',
  only_unlinked: false
};

const sortCarlines = (rows, sortBy) => {
  const sorted = [...rows];
  switch (sortBy) {
    case 'sop_asc': {
      const today = new Date();
      const upcoming = sorted.filter((carline) => carline.sop && new Date(carline.sop) > today);
      const others = sorted.filter((carline) => !carline.sop || new Date(carline.sop) <= today);
      upcoming.sort((a, b) => new Date(a.sop) - new Date(b.sop));
      others.sort((a, b) => {
        if (!a.sop && !b.sop) return (b.vol_cy_2025 || 0) - (a.vol_cy_2025 || 0);
        if (!a.sop) return 1;
        if (!b.sop) return -1;
        return new Date(b.sop) - new Date(a.sop);
      });
      return [...upcoming, ...others];
    }
    case 'sop_desc':
      return sorted.sort((a, b) => new Date(b.sop || '1900-01-01') - new Date(a.sop || '1900-01-01'));
    case 'volume_desc':
      return sorted.sort((a, b) => (b.avg_annual_volume || b.vol_cy_2025 || 0) - (a.avg_annual_volume || a.vol_cy_2025 || 0));
    case 'volume_asc':
      return sorted.sort((a, b) => (a.avg_annual_volume || a.vol_cy_2025 || 0) - (b.avg_annual_volume || b.vol_cy_2025 || 0));
    case 'lifecycle': {
      const order = { planning: 1, active: 2, ending: 3, ended: 4 };
      return sorted.sort((a, b) => (order[a.lifecycle_status] || 99) - (order[b.lifecycle_status] || 99));
    }
    default:
      return sorted;
  }
};

const getTimeToSOP = (sopDate) => {
  if (!sopDate) return null;
  const diffMonths = Math.round((new Date(sopDate) - new Date()) / (1000 * 60 * 60 * 24 * 30));
  if (diffMonths < 0) return { label: 'In Production', color: 'text-green-600' };
  if (diffMonths <= 6) return { label: `${diffMonths}mo away`, color: 'text-red-600' };
  if (diffMonths <= 12) return { label: `${diffMonths}mo away`, color: 'text-orange-600' };
  if (diffMonths <= 24) return { label: `${diffMonths}mo away`, color: 'text-blue-600' };
  return { label: `${Math.round(diffMonths / 12)}yr away`, color: 'text-gray-600' };
};

const lifecycleClass = (status) => {
  if (status === 'active') return 'bg-green-100 text-green-700';
  if (status === 'planning') return 'bg-blue-100 text-blue-700';
  if (status === 'ending') return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-100 text-gray-700';
};

const IHSForecastCatalog = ({
  mode = 'inline',
  opportunityId,
  onLink,
  onClose,
  onCreateOpportunity,
  allowCreateOpportunity = false,
  allowLink = false,
  title = 'IHS Market Data Browser',
  subtitle,
}) => {
  const [carlines, setCarlines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [sortBy, setSortBy] = useState('sop_asc');
  const [availableOEMs, setAvailableOEMs] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [linking, setLinking] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const isModal = mode === 'modal';
  const sortedCarlines = useMemo(() => sortCarlines(carlines, sortBy), [carlines, sortBy]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchFilterOptions = async () => {
    const [oemsRes, regionsRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/ihs-integration/available-oems`, { headers: authHeaders() }),
      axios.get(`${BACKEND_URL}/api/ihs-integration/available-regions`, { headers: authHeaders() })
    ]);
    setAvailableOEMs(oemsRes.data.oems || []);
    setAvailableRegions(regionsRes.data.regions || []);
  };

  const searchCarlines = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.oem) params.append('oem', filters.oem);
      if (filters.region) params.append('region', filters.region);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.lifecycle_status) params.append('lifecycle_status', filters.lifecycle_status);
      if (filters.only_unlinked) params.append('only_unlinked', 'true');
      params.append('limit', '50');

      const response = await axios.get(
        `${BACKEND_URL}/api/ihs-integration/search-carlines?${params.toString()}`,
        { headers: authHeaders() }
      );
      setCarlines(response.data || []);
    } catch (error) {
      console.error('Error searching carlines:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions().catch((error) => console.error('Error fetching filter options:', error));
    searchCarlines();
  }, []);

  const handleLinkCarline = async (carline) => {
    if (!opportunityId) return;
    setLinking((current) => ({ ...current, [carline.core_mnemonic]: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/ihs-integration/link-to-opportunity`,
        { opportunity_id: opportunityId, core_mnemonic: carline.core_mnemonic },
        { headers: authHeaders() }
      );
      onLink?.(carline);
      searchCarlines();
    } catch (error) {
      alert(`Failed to link: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLinking((current) => ({ ...current, [carline.core_mnemonic]: false }));
    }
  };

  const handleCreateOpportunity = async (carline) => {
    if (!window.confirm(`Create a new opportunity for ${carline.production_nameplate}?`)) return;
    setLinking((current) => ({ ...current, [carline.core_mnemonic]: true }));
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ihs-integration/create-opportunity-from-carline`,
        {
          core_mnemonic: parseInt(carline.core_mnemonic, 10),
          program_name: carline.production_nameplate || carline.program || 'New IHS Opportunity'
        },
        { headers: authHeaders() }
      );
      onCreateOpportunity?.(response.data);
      searchCarlines();
    } catch (error) {
      const errorMsg = error.response?.data?.detail
        || (typeof error.response?.data === 'string' ? error.response?.data : JSON.stringify(error.response?.data))
        || error.message;
      alert(`Failed to create opportunity: ${errorMsg}`);
    } finally {
      setLinking((current) => ({ ...current, [carline.core_mnemonic]: false }));
    }
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setSearchTerm('');
  };

  const content = (
    <div className={isModal ? 'bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col pointer-events-auto' : 'bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col'}>
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <div className="flex items-center">
            <Database className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1 ml-8">
            {subtitle || (opportunityId ? 'Search and link carlines to this opportunity' : 'Browse vehicle carlines from the market forecast')}
          </p>
        </div>
        {isModal && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      <div className="p-4 border-b border-gray-200">
        <form onSubmit={(event) => { event.preventDefault(); searchCarlines(); }} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by vehicle or nameplate..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Search
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="sop_asc">Upcoming SOP</option>
              <option value="volume_desc">Highest Volume</option>
              <option value="lifecycle">Lifecycle Stage</option>
              <option value="volume_asc">Lowest Volume</option>
              <option value="sop_desc">Latest SOP</option>
            </select>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">{sortedCarlines.length}</span> carlines found
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OEM</label>
              <select value={filters.oem} onChange={(event) => setFilters({ ...filters, oem: event.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">All OEMs</option>
                {availableOEMs.map((oem) => <option key={oem} value={oem}>{oem}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
              <select value={filters.region} onChange={(event) => setFilters({ ...filters, region: event.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">All Regions</option>
                {availableRegions.map((region) => <option key={region} value={region}>{region}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lifecycle</label>
              <select value={filters.lifecycle_status} onChange={(event) => setFilters({ ...filters, lifecycle_status: event.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">All Status</option>
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="ending">Ending</option>
                <option value="ended">Ended</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked={filters.only_unlinked} onChange={(event) => setFilters({ ...filters, only_unlinked: event.target.checked })} className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
                <span className="ml-2 text-sm text-gray-700">Only unlinked</span>
              </label>
            </div>
            <div className="col-span-2 md:col-span-4">
              <button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-700">Clear all filters</button>
            </div>
          </div>
        )}
      </div>

      <div className={`${isModal ? 'flex-1 overflow-y-auto' : ''} p-4`}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Searching...</p>
            </div>
          </div>
        ) : sortedCarlines.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No carlines found. Try adjusting your search or filters.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> {sortBy === 'sop_asc'
                  ? 'Carlines with future SOP dates are shown first, followed by active carlines.'
                  : 'Use filters to narrow the accepted IHS/S&P forecast catalog.'}
              </p>
            </div>
            <div className="space-y-3">
              {sortedCarlines.map((carline) => {
                const sopTiming = getTimeToSOP(carline.sop);
                return (
                  <div key={carline.core_mnemonic} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{carline.production_nameplate || carline.program}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${lifecycleClass(carline.lifecycle_status)}`}>
                            {carline.lifecycle_status || 'unknown'}
                          </span>
                          {carline.is_linked_to_opportunity && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">Linked</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600 mb-3">
                          <div><span className="font-medium">Vehicle:</span> {carline.program || 'N/A'}</div>
                          <div><span className="font-medium">OEM:</span> {carline.sales_parent}</div>
                          <div><span className="font-medium">Region:</span> {carline.region}</div>
                          <div><span className="font-medium">Platform:</span> {carline.platform || 'N/A'}</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-blue-600">
                              <TrendingUp className="w-5 h-5 mr-2" />
                              <div>
                                <p className="text-xs text-gray-600">Avg Annual Volume (Lifetime)</p>
                                <p className="text-lg font-bold">{(carline.avg_annual_volume || 0).toLocaleString()} units/yr</p>
                                {carline.total_lifetime_volume > 0 && (
                                  <p className="text-xs text-gray-500 mt-0.5">Total: {(carline.total_lifetime_volume || 0).toLocaleString()} units</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-600">Lifecycle</p>
                              <p className="text-sm font-semibold text-gray-900 capitalize">{carline.lifecycle_status || 'Unknown'}</p>
                            </div>
                          </div>
                          {(carline.sop || carline.eop) && (
                            <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                              {carline.sop && (
                                <div className="flex-1">
                                  <span className="text-gray-600">SOP:</span>
                                  <span className="ml-2 font-medium text-gray-900">{new Date(carline.sop).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</span>
                                  {sopTiming && <span className={`ml-2 font-bold ${sopTiming.color}`}>{sopTiming.label}</span>}
                                </div>
                              )}
                              {carline.eop && (
                                <div className="text-right">
                                  <span className="text-gray-600">EOP:</span>
                                  <span className="ml-2 font-medium text-gray-900">{new Date(carline.eop).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {allowLink && opportunityId && !carline.is_linked_to_opportunity && (
                          <button onClick={() => handleLinkCarline(carline)} disabled={linking[carline.core_mnemonic]} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2 whitespace-nowrap">
                            <LinkIcon className="w-4 h-4" />
                            {linking[carline.core_mnemonic] ? 'Linking...' : 'Link'}
                          </button>
                        )}
                        {allowCreateOpportunity && !opportunityId && !carline.is_linked_to_opportunity && (
                          <button onClick={() => handleCreateOpportunity(carline)} disabled={linking[carline.core_mnemonic]} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2 whitespace-nowrap">
                            <Plus className="w-4 h-4" />
                            {linking[carline.core_mnemonic] ? 'Creating...' : 'Add to Pipeline'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <p className="text-sm text-gray-600">
          Showing {sortedCarlines.length} carline{sortedCarlines.length !== 1 ? 's' : ''}
          {filters.only_unlinked && ' (unlinked only)'}
        </p>
      </div>
    </div>
  );

  if (!isModal) return content;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" onClick={onClose} style={{ pointerEvents: 'auto' }}>
      <div onClick={(event) => event.stopPropagation()} className="w-full max-w-6xl">
        {content}
      </div>
    </div>,
    document.body
  );
};

export default IHSForecastCatalog;
